import { useEffect, useRef, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { HelpCircle, X, AlertTriangle, Clock, MessageSquare, UserCheck, UserX } from 'lucide-react';

interface RTIExplanationProps {
  component: 'initiation' | 'latency' | 'emotionalDensity' | 'availability' | 'withdrawal';
  score: number;
  tier?: 'free' | 'basic' | 'premium' | 'pro';
}

export function RTIExplanation({ component, score, tier = 'free' }: RTIExplanationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [isOpen, component]);

  const getExplanation = () => {
    const isPremium = tier === 'basic' || tier === 'premium' || tier === 'pro';
    const isPro = tier === 'pro';

    switch (component) {
      case 'initiation':
        return {
          icon: <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />,
          title: 'Who starts contact',
          shortDesc: 'You vs. your partner — who texts or calls first most often?',
          score: score,
          interpretation:
            score > 70
              ? 'You’re starting most chats — they may be hanging back'
              : score > 50
                ? 'You start more than they do'
                : score > 30
                  ? 'Mostly balanced'
                  : 'Pretty even — both of you initiate',
          whyItMatters:
            'When one person always reaches out first, it can feel like a chase. It isn’t about who cares more — it’s a habit you can gently rebalance (e.g. wait for them to text sometimes).',
          premiumInsight: isPremium
            ? `At ${score}%, you’re in the ${score > 70 ? '“you reach out a lot”' : score > 50 ? '“you initiate more”' : 'balanced'} range. Many people see the other person step up more after they ease off a little for a couple of days — not as a game, but to reset the pattern.`
            : null,
          proInsight: isPro
            ? `If you’re always the one initiating, it often comes from worry they’ll slip away. Ironically, a bit less chasing can lower pressure and invite them toward you. Try one shorter day of “wait and see” when it feels safe.`
            : null,
          actionAdvice:
            score > 70
              ? '🎯 Try: For 48 hours, only reply unless they text first (skip if safety is a concern).'
              : score > 50
                ? '🎯 Try: Let them start the next conversation once or twice before you do.'
                : '🎯 Keep this balance — it looks healthy.',
        };

      case 'latency':
        return {
          icon: <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />,
          title: 'Reply speed',
          shortDesc: 'Are they answering slower than their usual?',
          score: score,
          interpretation:
            score > 70
              ? 'Replies are much slower than before'
              : score > 50
                ? 'Clearly slower than usual'
                : score > 30
                  ? 'A bit slower'
                  : 'About the same as usual',
          whyItMatters:
            'This isn’t about minutes on a clock — it’s about change from *their* normal. If they used to answer quickly and now they don’t, that shift can mean they’re distracted, overwhelmed, or pulling back emotionally.',
          premiumInsight: isPremium
            ? `Your score is ${score}. When reply times stretch, people often notice other distance signs within a few days. Use it as a signal to match their pace — not to flood them with messages.`
            : null,
          proInsight: isPro
            ? `If replies slow down *and* messages feel shorter or colder, treat it as a whole picture — not just “they’re busy.” One calm check-in beats a string of pings.`
            : null,
          actionAdvice:
            score > 70
              ? '🎯 Don’t double-text. Mirror their timing when you reply.'
              : score > 50
                ? '🎯 Space out your messages a little; let breathing room exist.'
                : '🎯 No big change needed here.',
        };

      case 'emotionalDensity':
        return {
          icon: <MessageSquare className="h-5 w-5 text-purple-600 dark:text-purple-400" />,
          title: 'Warmth in messages',
          shortDesc: 'Do their messages feel shorter, colder, or less affectionate?',
          score: score,
          interpretation:
            score > 70
              ? 'Very flat — little warmth or detail'
              : score > 50
                ? 'Noticeably less warm than before'
                : score > 30
                  ? 'Slightly less expressive'
                  : 'Still sounds engaged and warm',
          whyItMatters:
            'Warmth usually drops before someone says “we need space.” Long messages becoming “ok” or heart emojis disappearing can mean they’re easing back emotionally.',
          premiumInsight: isPremium
            ? `At ${score}%, ${score > 70 ? 'the drop is strong' : score > 50 ? 'you’re seeing real cooling' : 'change is mild'}. Noticing early helps you respond with calm, not panic.`
            : null,
          proInsight: isPro
            ? `If they go brief, matching that tone for a bit often works better than “What’s wrong??” Pressure can make them shut down more.`
            : null,
          actionAdvice:
            score > 70
              ? '🎯 Keep your replies short and light too — don’t demand a feelings talk by text.'
              : score > 50
                ? '🎯 Dial back intensity slightly; mirror their energy.'
                : '🎯 You’re fine to keep chatting as usual.',
        };

      case 'availability':
        return {
          icon: <UserCheck className="h-5 w-5 text-green-600 dark:text-green-400" />,
          title: 'Always free for them',
          shortDesc: 'Do you drop plans or answer instantly every time?',
          score: score,
          interpretation:
            score > 70
              ? 'You’re almost always on-call for them'
              : score > 50
                ? 'You put them first very often'
                : score > 30
                  ? 'Somewhat flexible'
                  : 'Healthy boundaries — you have your own life too',
          whyItMatters:
            'Being super available can feel loving, but it can also make the relationship feel one-sided. A little healthy “I’m busy then” can actually help balance respect and attraction.',
          premiumInsight: isPremium
            ? `Your score is ${score}. People who keep one or two non-negotiable plans (friends, gym, work) often see their partner value their time more — not less.`
            : null,
          proInsight: isPro
            ? `If you’re always “yes” out of fear they’ll leave, you’re not failing — you’re anxious. Small nos (when safe) rebuild self-respect and often calm the dynamic.`
            : null,
          actionAdvice:
            score > 70
              ? '🎯 Say no to the next request once if you can — keep a plan you already had.'
              : score > 50
                ? '🎯 Protect one block of time this week just for you.'
                : '🎯 Keep this balance.',
        };

      case 'withdrawal':
        return {
          icon: <UserX className="h-5 w-5 text-red-600 dark:text-red-400" />,
          title: 'Pulling away',
          shortDesc: 'Silence, canceled plans, or avoiding contact?',
          score: score,
          interpretation:
            score > 70
              ? 'Strong withdrawal — hard to reach'
              : score > 50
                ? 'Clear distance'
                : score > 30
                  ? 'Some avoidance'
                  : 'Normal contact',
          whyItMatters:
            'This is the heaviest signal: active avoidance, not just slow replies. It can mean they need space, they’re overwhelmed, or they’re unsure about the relationship.',
          premiumInsight: isPremium
            ? `Score ${score}. If you’re chasing hard while they withdraw, the classic “push–pull” loop can worsen. Pausing your side can help more than pressure.`
            : null,
          proInsight: isPro
            ? `Long silence plus your panic texts often speeds up endings. If you can, give real space, lean on friends, and let them surface when ready — unless safety requires otherwise.`
            : null,
          actionAdvice:
            score > 70
              ? '🎯 Don’t flood them. Minimum contact for 48–72h unless urgent; then one short, kind message.'
              : score > 50
                ? '🎯 Cut contact volume roughly in half; wait for them to lean in.'
                : '🎯 Watch and stay steady — no big move needed yet.',
        };

      default:
        return null;
    }
  };

  const explanation = getExplanation();
  if (!explanation) return null;

  const getScoreColor = (s: number) => {
    if (s > 70) return 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/50 border-red-200 dark:border-red-800';
    if (s > 50) return 'text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-950/40 border-orange-200 dark:border-orange-800';
    if (s > 30) return 'text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800';
    return 'text-green-800 dark:text-green-200 bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800';
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="rounded-full p-1 text-neutral-600 transition-colors hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
        aria-label="Explain this component"
      >
        <HelpCircle className="h-4 w-4" />
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div
            ref={scrollRef}
            className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-xl shadow-xl"
          >
          <Card
            className="border-neutral-200 bg-white text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
          >
            <div className="sticky top-0 z-10 flex items-start justify-between border-b border-neutral-200 bg-white p-6 dark:border-neutral-700 dark:bg-neutral-900">
              <div className="flex items-center gap-3">
                {explanation.icon}
                <div>
                  <h3 className="font-medium text-neutral-900 dark:text-neutral-50">{explanation.title}</h3>
                  <p className="text-sm text-neutral-600 dark:text-neutral-300">{explanation.shortDesc}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-lg p-1 text-neutral-600 transition-colors hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6 p-6">
              <div className={`tension-glow rounded-lg border p-4 ${getScoreColor(explanation.score)}`}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium">Your score</span>
                  <span className="text-2xl font-light tabular-nums">{explanation.score}</span>
                </div>
                <p className="text-sm font-medium">{explanation.interpretation}</p>
              </div>

              <div>
                <h4 className="mb-2 font-medium text-neutral-900 dark:text-neutral-100">Why this matters</h4>
                <p className="text-sm leading-relaxed text-neutral-800 dark:text-neutral-200">
                  {explanation.whyItMatters}
                </p>
              </div>

              {explanation.premiumInsight && (
                <div className="tension-glow rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/40">
                  <h4 className="mb-2 flex items-center gap-2 font-medium text-blue-950 dark:text-blue-100">
                    <span className="rounded-full bg-blue-600 px-2 py-0.5 text-xs text-white">Premium</span>
                    What this usually means
                  </h4>
                  <p className="text-sm leading-relaxed text-blue-900 dark:text-blue-100/95">
                    {explanation.premiumInsight}
                  </p>
                </div>
              )}

              {explanation.proInsight && (
                <div className="tension-glow rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-800 dark:bg-purple-950/40">
                  <h4 className="mb-2 flex items-center gap-2 font-medium text-purple-950 dark:text-purple-100">
                    <span className="rounded-full bg-purple-600 px-2 py-0.5 text-xs text-white">Pro</span>
                    Deeper note
                  </h4>
                  <p className="text-sm leading-relaxed text-purple-900 dark:text-purple-100/95">
                    {explanation.proInsight}
                  </p>
                </div>
              )}

              <div className="rounded-lg bg-neutral-900 p-4 text-white dark:bg-neutral-950">
                <h4 className="mb-2 font-medium">Try this</h4>
                <p className="text-sm leading-relaxed text-neutral-100">{explanation.actionAdvice}</p>
              </div>

              <Button type="button" variant="outline" className="w-full text-neutral-900 dark:text-neutral-100" onClick={() => setIsOpen(false)}>
                Close
              </Button>
            </div>
          </Card>
          </div>
        </div>
      )}
    </div>
  );
}
